import React, { useEffect, useState } from "react";
import "./AuthPage.css";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation } from "./services/auth";
import { useDispatch } from "react-redux";
import { userApi } from "./services/user";
import { setAuthToken } from "./services/GlobalSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthLeftPanel from "./component/AuthLeftPanel";

const isTokenValid = (tokenKey = "access_token") => {
  const token = localStorage.getItem(tokenKey);
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 <= Date.now()) {
      localStorage.removeItem(tokenKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [showPendingMessage, setShowPendingMessage] = useState(false);

  useEffect(() => {
    if (isTokenValid("access_token")) {
      navigate("/chat", { replace: true });
    }
  }, [navigate]);

  const notifySuccess = (message) => toast.success(message);
  const notifyError = (message) => toast.error(message);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowPendingMessage(false);

    try {
      const response = await login({ email, password }).unwrap();
      localStorage.setItem("hasLogin", true);
      localStorage.setItem("access_token", response?.access_token);
      localStorage.setItem("refresh_token", response?.refresh_token);
      dispatch(setAuthToken(response?.access_token));
      dispatch(userApi.util.resetApiState());

      notifySuccess("Login successful! Redirecting...");

      setTimeout(() => {
        navigate("/chat");
      }, 1500);
    } catch (err) {
      if (err?.status === 403) {
        const data = err.data || {};

        if (data.error === "email_not_verified") {
          setError(
            "Please verify your email address before signing in. Check your inbox."
          );
        } else if (data.error === "pending_approval") {
          setError(null);
          setShowPendingMessage(true);
        } else if (data.error === "account_inactive") {
          setError(
            "Your account is not active. Contact support@stratusedge.ai"
          );
        } else {
          setError(data.message || "Access denied. Please contact support.");
        }
      } else {
        notifyError("Invalid credentials. Please try again.");
      }
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  return (
    <>
      <nav className="auth-nav">
        <Link to="/" className="auth-nav-logo">
          <img
            src="/StratusEdgeFinal_whiteupscaled.png"
            alt="Edge AI"
            className="auth-nav-logo-img"
          />
        </Link>
      </nav>
      <div className="auth-split">
      <AuthLeftPanel variant="login" />
      <div className="auth-split-border" aria-hidden="true" />

      <div className="auth-split-right">
        <div className="auth-form-shell auth-form-animate">
          <h2 className="auth-heading">Welcome back</h2>
          <p className="auth-subheading">Sign in to continue to your workspace.</p>

          {showPendingMessage ? (
            <div className="auth-pending-state">
              <div className="auth-pending-icon">⏳</div>
              <h3>Account pending approval</h3>
              <p>
                Your account is currently under review. You'll receive an email
                at <strong>{email}</strong> once approved.
              </p>
              <p className="auth-pending-hint">
                Usually approved within 1 business day. Questions? Email{" "}
                <a href="mailto:support@stratusedge.ai">support@stratusedge.ai</a>
              </p>
              <button
                type="button"
                className="auth-button-secondary"
                onClick={() => setShowPendingMessage(false)}
              >
                Back to sign in
              </button>
            </div>
          ) : (
          <>
          {error && <div className="auth-error-banner">{error}</div>}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-input-container">
              <label htmlFor="login-email">Email Address</label>
            </div>
            <input
              id="login-email"
              type="email"
              placeholder="you@company.com"
              className="auth-input auth-input-lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <div className="auth-input-container mt-2">
              <label htmlFor="login-password">Password</label>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="auth-input auth-input-lg password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={showPassword ? "bi bi-eye" : "bi bi-eye-slash"}></i>
              </button>
            </div>

            <button className="auth-button auth-button-primary" disabled={loading}>
              {loading ? (
                <span className="auth-button-loading">
                  <span className="auth-spinner" aria-hidden="true" />
                  Signing in…
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="auth-links-row">
            <Link to="/forgot-password" className="auth-link-inline">
              Forgot password?
            </Link>
          </div>

          <div className="auth-footer">
            <span>Don’t have an account?</span>{" "}
            <Link to="/register" className="auth-link-inline">
              Sign up
            </Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;
